import moment from 'moment-timezone';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { primaryShadow } from '@hivebrite/design-system/Theme';

import EventCanceledLabel from 'hb-react/shared/apps/Events/components/EventCanceledLabel';
import useEventDisplayTimeZone from 'hb-react/shared/components/EventDate/hooks/useEventDisplayTimeZone';
import type { StorageAttachmentRecordSnake } from 'hb-react/shared/hooks/useGlobalStorageV2/types';
import TwoDigitsCalendar from 'hb-react/shared/icons/dynamic/TwoDigitsCalendar';

import { EventLink } from '../EventLink';

export const Event = ({
  id,
  title,
  type,
  cancelled,
  groupName,
  new_cover_picture: newCoverPicture,
  start_date: startDate,
  timezone,
}: {
  id: number;
  title: string;
  type: string;
  cancelled: boolean;
  groupName: string;
  new_cover_picture: StorageAttachmentRecordSnake;
  start_date: string;
  timezone: string;
}) => {
  const eventDisplayTimeZone = useEventDisplayTimeZone(timezone);
  const date = moment(startDate).tz(eventDisplayTimeZone);

  return (
    <Box>
      <DateCalendar>
        <Month>{date.format('MMM').toUpperCase()}</Month>
        <Box color="primary">
          <StyledTwoDigitsCalendar day={date.date()} height={42} width={42} />
        </Box>
      </DateCalendar>
      <Box position="relative">
        <Image
          $src={newCoverPicture.preview_url || newCoverPicture.medium_url}
        />
        <Box position="absolute" top={2} left={6}>
          <EventCanceledLabel event={{ cancelled }} />
        </Box>
      </Box>
      <Info>
        <Title>
          <StyledDotdotdot clamp={2}>{title}</StyledDotdotdot>
        </Title>
        {groupName && (
          <GroupName>
            <StyledDotdotdot clamp={1}>{groupName}</StyledDotdotdot>
          </GroupName>
        )}
        <EventLink event={{ id, type, title }} />
      </Info>
    </Box>
  );
};

const DateCalendar = styled(Box).attrs(() => ({
  flexDirection: 'column',
}))``;

const Month = styled(Box).attrs(() => ({
  justifyContent: 'center',
  mb: '5px',
}))``;

const StyledTwoDigitsCalendar = styled(TwoDigitsCalendar)`
  ${primaryShadow}
`;

const Image = styled(Box).attrs<{ $src: string }>(({ $src }) => ({
  style: {
    backgroundImage: `url(${$src})`,
  },
  height: { _: 59, xl: 78 },
  width: { _: 112, xl: 148 },
  minWidth: { _: 112, xl: 148 },
  ml: 4,
  mr: 3,
}))<{ $src: string }>`
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  border-radius: 4px;
`;

const Info = styled(Box).attrs(() => ({
  flexDirection: 'column',
}))``;

const Title = styled(Box).attrs(() => ({
  font: 'h3-semibold',
}))``;

const GroupName = styled(Box).attrs(() => ({
  font: 'metadata-regular-asphalt',
  mt: 1,
}))``;

const StyledDotdotdot = styled(Dotdotdot)`
  word-break: break-word;
`;
